import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';

const Header = () => {
  const [darkMode, setDarkMode] = useState(() => {
    return document.documentElement.classList.contains('dark');
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname;

  const toggleDark = () => {
    const next = !darkMode;
    setDarkMode(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('darkMode', next ? 'true' : 'false');
  };

  const getDesktopLinkClass = (path: string) => {
    const isActive = currentPath === path;
    return `px-3 py-2 rounded-md transition-colors font-medium ${
      isActive 
        ? 'text-primary-700 bg-primary-50 dark:text-primary-400 dark:bg-gray-800'
        : 'text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-800'
    }`;
  };

  const getMobileLinkClass = (path: string) => {
    const isActive = currentPath === path;
    return `block px-3 py-2 rounded-md text-base font-medium ${
      isActive
        ? 'text-primary-700 bg-primary-50 dark:text-primary-400 dark:bg-gray-800'
        : 'text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'
    }`;
  };

  return (
    <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo e título */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <svg 
                className="w-5 h-5 text-white" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M13 10V3L4 14h7v7l9-11h-7z" 
                />
              </svg>
            </div>
            
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                MelBot
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Controle do CPM
              </p>
            </div>
          </Link>


          {/* Navegação Desktop */}
          <nav className="hidden md:flex items-center space-x-2">
            <Link 
              to="/" 
              className={getDesktopLinkClass('/')}
            >
              🤖 BotLead
            </Link>
            
            <Link 
              to="/leads" 
              className={getDesktopLinkClass('/leads')}
            >
              Leads
            </Link>

            <Link 
              to="/catalog" 
              className={getDesktopLinkClass('/catalog')}
            >
              📦 BotCatálogo
            </Link>

            <Link 
              to="/logs" 
              className={getDesktopLinkClass('/logs')}
            >
              Logs
            </Link>

            <Link 
              to="/sales" 
              className={getDesktopLinkClass('/sales')}
            >
              ⭐ Vendas
            </Link>

            <Link 
              to="/chat" 
              className={getDesktopLinkClass('/chat')}
            >
              🤖 Chat
            </Link>

            <Link 
              to="/crm" 
              className={getDesktopLinkClass('/crm')}
            >
              📋 CRM
            </Link>
            
            <Link 
              to="/war-room" 
              className={getDesktopLinkClass('/war-room')}
            >
              ⚡ WAR ROOM
            </Link>
          </nav>

          {/* User + Dark Mode + Mobile Toggle */}
          <div className="flex items-center space-x-4">
            {/* Dark mode toggle */}
            <button
              onClick={toggleDark}
              className="p-2 mr-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title={darkMode ? 'Modo claro' : 'Modo escuro'}
            >
              {darkMode ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
            </button>
            
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button 
                className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? (
                   <span className="block w-6 h-6 text-2xl leading-none">&times;</span> 
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M4 6h16M4 12h16M4 18h16" 
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu Dropdown */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
             <Link 
              to="/" 
              className={getMobileLinkClass('/')}
              onClick={() => setIsMenuOpen(false)}
             >
              🤖 BotLead
            </Link>
            <Link 
              to="/leads" 
              className={getMobileLinkClass('/leads')}
              onClick={() => setIsMenuOpen(false)}
             >
              Leads
            </Link>
            <Link 
              to="/catalog" 
              className={getMobileLinkClass('/catalog')}
              onClick={() => setIsMenuOpen(false)}
             >
              📦 BotCatálogo
            </Link>
            <Link 
              to="/logs" 
              className={getMobileLinkClass('/logs')}
              onClick={() => setIsMenuOpen(false)}
             >
              Logs
            </Link>
            <Link 
              to="/sales" 
              className={getMobileLinkClass('/sales')}
              onClick={() => setIsMenuOpen(false)}
             >
              ⭐ Vendas
            </Link>
            <Link 
              to="/chat" 
              className={getMobileLinkClass('/chat')}
              onClick={() => setIsMenuOpen(false)}
             >
              🤖 Chat
            </Link>
            <Link 
              to="/crm" 
              className={getMobileLinkClass('/crm')}
              onClick={() => setIsMenuOpen(false)}
             >
              📋 CRM
            </Link>
            
            <Link 
              to="/war-room" 
              className={getMobileLinkClass('/war-room')}
              onClick={() => setIsMenuOpen(false)}
             >
              ⚡ WAR ROOM
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;